import React, { useMemo, useState } from 'react';
import { Users, Check, X, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';

// Rollen, die im Mitarbeiter-Filter nicht auftauchen sollen
const HIDDEN_ROLES = ['influencer'];

const initials = (u) => `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.toUpperCase();
const fullName = (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim();

/**
 * Mitarbeiter-Filter fuer Admins (Tasks-Seite).
 * Mehrfachauswahl mit Suche, ausgewaehlte Mitarbeiter als Chips daneben.
 */
const TeamMemberFilter = ({ users, selectedIds, onChange, loading = false }) => {
    const [open, setOpen] = useState(false);

    const selectable = useMemo(
        () => (users || [])
            .filter(u => u.is_active !== false && !HIDDEN_ROLES.includes(u.role))
            .sort((a, b) => fullName(a).localeCompare(fullName(b), 'de')),
        [users]
    );

    const selectedUsers = useMemo(
        () => selectedIds.map(id => selectable.find(u => u.id === id)).filter(Boolean),
        [selectedIds, selectable]
    );

    const toggle = (id) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter(x => x !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    return (
        <div className="flex items-center gap-2 flex-wrap" data-testid="team-member-filter">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant={selectedIds.length > 0 ? 'default' : 'outline'}
                        size="sm"
                        className="gap-1.5"
                        data-testid="team-member-filter-trigger"
                    >
                        <Users className="w-4 h-4" />
                        <span className="hidden sm:inline">
                            {selectedIds.length > 0 ? `Mitarbeiter (${selectedIds.length})` : 'Mitarbeiter'}
                        </span>
                        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                    <Command>
                        <CommandInput placeholder="Mitarbeiter suchen..." data-testid="team-member-filter-search" />
                        <CommandList>
                            <CommandEmpty>Kein Mitarbeiter gefunden.</CommandEmpty>
                            <CommandGroup>
                                {selectable.map(u => {
                                    const active = selectedIds.includes(u.id);
                                    return (
                                        <CommandItem
                                            key={u.id}
                                            value={`${fullName(u)} ${u.username || ''}`}
                                            onSelect={() => toggle(u.id)}
                                            className="gap-2 cursor-pointer"
                                            data-testid={`team-member-option-${u.id}`}
                                        >
                                            <Avatar className="w-6 h-6">
                                                {u.profile_image ? <AvatarImage src={u.profile_image} /> : null}
                                                <AvatarFallback
                                                    style={{ backgroundColor: u.profile_color }}
                                                    className="text-white text-[10px]"
                                                >
                                                    {initials(u)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span className="flex-1 truncate">{fullName(u)}</span>
                                            <Check className={`w-4 h-4 ${active ? 'opacity-100 text-primary' : 'opacity-0'}`} />
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </CommandList>
                        {selectedIds.length > 0 && (
                            <div className="border-t border-border p-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="w-full text-muted-foreground"
                                    onClick={() => { onChange([]); setOpen(false); }}
                                    data-testid="team-member-filter-clear"
                                >
                                    <X className="w-3.5 h-3.5 mr-1.5" />
                                    Auswahl aufheben
                                </Button>
                            </div>
                        )}
                    </Command>
                </PopoverContent>
            </Popover>

            {/* Chips der ausgewaehlten Mitarbeiter - Klick entfernt sie wieder */}
            {selectedUsers.map(u => (
                <button
                    key={u.id}
                    type="button"
                    onClick={() => toggle(u.id)}
                    title={`${fullName(u)} entfernen`}
                    className="flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full border text-xs font-medium hover:bg-muted/60 transition-colors"
                    style={{ borderColor: u.profile_color }}
                    data-testid={`team-member-chip-${u.id}`}
                >
                    <Avatar className="w-5 h-5">
                        {u.profile_image ? <AvatarImage src={u.profile_image} /> : null}
                        <AvatarFallback style={{ backgroundColor: u.profile_color }} className="text-white text-[9px]">
                            {initials(u)}
                        </AvatarFallback>
                    </Avatar>
                    <span>{u.first_name}</span>
                    <X className="w-3 h-3 opacity-60" />
                </button>
            ))}

            {loading && (
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            )}
        </div>
    );
};

export default TeamMemberFilter;
